import React, { useState, useEffect, useCallback } from 'react';
import { getConversionFunnel } from '../../services/api';
import {
  PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const TIER_COLORS = {
  free: '#6b7280',
  essential: '#218aff',
  plus: '#10b981',
  premium: '#8b5cf6',
};

function getKpiColor(value, thresholds) {
  if (value >= thresholds.green) return '#10b981';
  if (value >= thresholds.yellow) return '#f59e0b';
  return '#ef4444';
}

function formatTimeToFirstUse(hours) {
  if (hours === null || hours === undefined) return 'N/A';
  if (hours >= 24) {
    const days = Math.round(hours / 24);
    return `${days} day${days !== 1 ? 's' : ''}`;
  }
  return `${Math.round(hours)}h`;
}

function ReportsConversionFunnel() {
  const [funnelData, setFunnelData] = useState(null);
  const [period, setPeriod] = useState('90d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getConversionFunnel(period);
      setFunnelData(result);
    } catch (err) {
      setError(err.message || 'Failed to fetch conversion funnel data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePeriodChange = (e) => {
    setPeriod(e.target.value);
  };

  if (loading) {
    return (
      <div className="roles-loading">
        <div className="spinner"></div>
        <p>Loading conversion funnel data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reports-error">
        <p>{error}</p>
        <button className="refresh-btn" onClick={fetchData}>Retry</button>
      </div>
    );
  }

  const funnel = funnelData?.funnel || { signups: 0, activated: 0, converted: 0, churned: 0 };
  const tierBreakdown = funnelData?.tier_breakdown || [];
  const avgTimeToFirstUse = funnelData?.avg_time_to_first_use_hours;
  const signupsOverTime = funnelData?.signups_over_time || [];

  const signups = funnel.signups || 0;
  const activated = funnel.activated || 0;
  const converted = funnel.converted || 0;
  const churned = funnel.churned || 0;

  const activationRate = signups > 0 ? (activated / signups) * 100 : 0;
  const conversionRate = signups > 0 ? (converted / signups) * 100 : 0;

  const activatedPct = signups > 0 ? (activated / signups) * 100 : 0;
  const convertedPct = signups > 0 ? (converted / signups) * 100 : 0;

  const dropoffSignupToActivated = signups > 0 ? ((signups - activated) / signups) * 100 : 0;
  const dropoffActivatedToConverted = signups > 0 ? ((activated - converted) / signups) * 100 : 0;

  // Prepare pie chart data
  const pieData = tierBreakdown.map((tier) => ({
    name: tier.subscription_tier__display_name || tier.subscription_tier__name || 'Unknown',
    value: tier.count,
    tierKey: (tier.subscription_tier__name || 'free').toLowerCase(),
  }));

  // Prepare signups over time data
  const areaData = signupsOverTime.map((entry) => ({
    date: entry.date,
    count: entry.count,
  }));

  const formatDateTick = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[1]}/${parts[2]}`;
    }
    return dateStr;
  };

  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '6px',
          padding: '8px 12px',
          color: '#fff',
          fontSize: '13px',
        }}>
          <p style={{ margin: 0 }}>{data.name}: {data.value}</p>
        </div>
      );
    }
    return null;
  };

  const CustomAreaTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '6px',
          padding: '8px 12px',
          color: '#fff',
          fontSize: '13px',
        }}>
          <p style={{ margin: 0, marginBottom: '4px', color: '#aaa' }}>{label}</p>
          <p style={{ margin: 0 }}>Signups: {payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = ({ name, value, cx, cy, midAngle, outerRadius }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 24;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#fff"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
      >
        {name} ({value})
      </text>
    );
  };

  return (
    <div className="reports-conversion-funnel">
      {/* Header with Period Selector */}
      <div className="reports-section-header">
        <h3>Conversion Funnel</h3>
        <div className="period-selector">
          <label htmlFor="funnel-period-select">Time Period:</label>
          <select
            id="funnel-period-select"
            value={period}
            onChange={handlePeriodChange}
            className="period-select"
          >
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="180d">Last 180 Days</option>
            <option value="all">All Time</option>
          </select>
          <button className="refresh-btn" onClick={fetchData} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      {/* Conversion Funnel Visualization */}
      <div className="analytics-funnel-container" style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '24px',
        maxWidth: '700px',
      }}>
        <h4 style={{ color: '#fff', margin: '0 0 20px 0', fontSize: '16px' }}>
          Signup to Subscription Funnel
        </h4>

        {/* Stage 1: Signed Up */}
        <div className="analytics-funnel-stage" style={{
          width: '100%',
          height: '50px',
          backgroundColor: '#218aff',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          marginBottom: '4px',
          boxSizing: 'border-box',
        }}>
          <span style={{ color: '#fff', fontWeight: 600, fontSize: '14px' }}>Signed Up</span>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: '16px' }}>{signups}</span>
        </div>

        {/* Drop-off: Signup to Activated */}
        <div className="analytics-funnel-dropoff" style={{
          padding: '6px 0 6px 12px',
          fontSize: '12px',
          color: '#ef4444',
          marginBottom: '4px',
        }}>
          {dropoffSignupToActivated.toFixed(1)}% dropped off
        </div>

        {/* Stage 2: Activated */}
        <div className="analytics-funnel-stage" style={{
          width: signups > 0 ? `${Math.max((activated / signups) * 100, 10)}%` : '10%',
          height: '50px',
          backgroundColor: '#10b981',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          marginBottom: '4px',
          boxSizing: 'border-box',
          minWidth: '180px',
        }}>
          <span style={{ color: '#fff', fontWeight: 600, fontSize: '14px' }}>Activated</span>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
            {activated} ({activatedPct.toFixed(1)}%)
          </span>
        </div>

        {/* Drop-off: Activated to Converted */}
        <div className="analytics-funnel-dropoff" style={{
          padding: '6px 0 6px 12px',
          fontSize: '12px',
          color: '#ef4444',
          marginBottom: '4px',
        }}>
          {dropoffActivatedToConverted.toFixed(1)}% dropped off
        </div>

        {/* Stage 3: Subscribed */}
        <div className="analytics-funnel-stage" style={{
          width: signups > 0 ? `${Math.max((converted / signups) * 100, 10)}%` : '10%',
          height: '50px',
          backgroundColor: '#8b5cf6',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          boxSizing: 'border-box',
          minWidth: '180px',
        }}>
          <span style={{ color: '#fff', fontWeight: 600, fontSize: '14px' }}>Subscribed</span>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
            {converted} ({convertedPct.toFixed(1)}%)
          </span>
        </div>
      </div>

      {/* KPI Cards Row */}
      <div className="analytics-kpi-cards" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
      }}>
        {/* Activation Rate */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '16px',
          textAlign: 'center',
        }}>
          <div style={{ color: '#aaa', fontSize: '12px', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Activation Rate
          </div>
          <div style={{
            fontSize: '28px',
            fontWeight: 700,
            color: getKpiColor(activationRate, { green: 50, yellow: 20 }),
          }}>
            {activationRate.toFixed(1)}%
          </div>
          <div style={{ color: '#666', fontSize: '11px', marginTop: '4px' }}>
            {activated} of {signups} signups
          </div>
        </div>

        {/* Conversion Rate */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '16px',
          textAlign: 'center',
        }}>
          <div style={{ color: '#aaa', fontSize: '12px', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Conversion Rate
          </div>
          <div style={{
            fontSize: '28px',
            fontWeight: 700,
            color: getKpiColor(conversionRate, { green: 5, yellow: 1 }),
          }}>
            {conversionRate.toFixed(1)}%
          </div>
          <div style={{ color: '#666', fontSize: '11px', marginTop: '4px' }}>
            {converted} of {signups} signups
          </div>
        </div>

        {/* Avg Time to First Use */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '16px',
          textAlign: 'center',
        }}>
          <div style={{ color: '#aaa', fontSize: '12px', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Avg Time to First Use
          </div>
          <div style={{
            fontSize: '28px',
            fontWeight: 700,
            color: '#218aff',
          }}>
            {formatTimeToFirstUse(avgTimeToFirstUse)}
          </div>
          <div style={{ color: '#666', fontSize: '11px', marginTop: '4px' }}>
            from signup to first action
          </div>
        </div>

        {/* Churned Users */}
        <div style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '16px',
          textAlign: 'center',
        }}>
          <div style={{ color: '#aaa', fontSize: '12px', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Churned Users
          </div>
          <div style={{
            fontSize: '28px',
            fontWeight: 700,
            color: '#ef4444',
          }}>
            {churned}
          </div>
          <div style={{ color: '#666', fontSize: '11px', marginTop: '4px' }}>
            cancelled or inactive
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '24px',
        marginBottom: '24px',
      }}>
        {/* Tier Distribution Pie Chart */}
        <div className="analytics-chart-container" style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '16px',
        }}>
          <h4 style={{ color: '#fff', margin: '0 0 12px 0', fontSize: '14px' }}>
            Tier Distribution
          </h4>
          {pieData.length === 0 ? (
            <p style={{ color: '#666', textAlign: 'center', padding: '40px 0' }}>
              No tier data available.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  dataKey="value"
                  label={renderCustomLabel}
                  labelLine={{ stroke: '#555', strokeWidth: 1 }}
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={TIER_COLORS[entry.tierKey] || '#6b7280'}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomPieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Signups Over Time Area Chart */}
        <div className="analytics-chart-container" style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '16px',
        }}>
          <h4 style={{ color: '#fff', margin: '0 0 12px 0', fontSize: '14px' }}>
            Signups Over Time
          </h4>
          {areaData.length === 0 ? (
            <p style={{ color: '#666', textAlign: 'center', padding: '40px 0' }}>
              No signup data available.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={areaData}>
                <defs>
                  <linearGradient id="signupFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#218aff" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#218aff" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDateTick}
                  stroke="#666"
                  tick={{ fill: '#aaa', fontSize: 11 }}
                />
                <YAxis
                  stroke="#666"
                  tick={{ fill: '#aaa', fontSize: 11 }}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomAreaTooltip />} />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#218aff"
                  strokeWidth={2}
                  fill="url(#signupFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReportsConversionFunnel;
